import React, { useState, useEffect } from "react";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
  where,
  onSnapshot,
  addDoc,
  runTransaction
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import ReservationPDF from './ReservationPDF';
import generatePaymentReceiptPDF from './PaymentReceiptGenerator';
import GuestBillPDF from './GuestBillPDF';

Modal.setAppElement('#root');

const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

const Reservation = () => {
  const { user, userData } = useUserAuth();
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  // const [selectedStatus, setSelectedStatus] = useState('');
  // const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [availableEditRooms, setAvailableEditRooms] = useState([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false);
  const [isCheckOutModalOpen, setIsCheckOutModalOpen] = useState(false);
  const [isCancelConfirmModalOpen, setIsCancelConfirmModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [incidentalTotal, setIncidentalTotal] = useState(0);
  const [linkedSales, setLinkedSales] = useState([]);
  const [almostDueCheckIns, setAlmostDueCheckIns] = useState([]);
  const [almostDueCheckOuts, setAlmostDueCheckOuts] = useState([]);
  const [overdueCheckIns, setOverdueCheckIns] = useState([]);
  const [overdueCheckOuts, setOverdueCheckOuts] = useState([]);
  const [showOverduePopup, setShowOverduePopup] = useState(false);
  const [expiredBookings, setExpiredBookings] = useState([]);
  const [isSettleToAccountModalOpen, setIsSettleToAccountModalOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [hotelAccounts, setHotelAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [transferMemo, setTransferMemo] = useState('Balance transferred at checkout');
  const [isVoidConfirmModalOpen, setIsVoidConfirmModalOpen] = useState(false);
  const [paymentToVoid, setPaymentToVoid] = useState(null);
  // Payment modal states
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [payerName, setPayerName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Transfer');
  const [paymentNotes, setPaymentNotes] = useState('');

  // Payment History states
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [fetchingHistory, setFetchingHistory] = useState(false);

  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');

  const [selectedPaymentAccountId, setSelectedPaymentAccountId] = useState('');

  // Edit form states
  const [editForm, setEditForm] = useState({
    guestName: '',
    guestPhone: '',
    guestEmail: '',
    checkIn: null,
    checkOut: null,
    roomId: '',
    ratePlanId: '',
    totalPrice: 0,
    nights: 0,
    deposit: 0,
    notes: '',
    selectedAmenities: [],
    nightlyRate: 0 
  });

  const bookingsCollectionRef = collection(db, "bookings");
  const roomsCollectionRef = collection(db, "rooms");
  const roomTypesCollectionRef = collection(db, "roomTypes");
  const ratePlansCollectionRef = collection(db, "ratePlans");

  const TIME_WINDOWS = {
    checkInWarningHours:  4,     // notify when < 4 hours until check-in
    checkInUrgentHours:   1,     // red/highlight when < 1 hour
    checkOutWarningHours: 6,     // notify when < 6 hours until checkout
    checkOutUrgentHours:  2,     // urgent when < 2 hours
  };

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const q = query(collection(db, "accounts"), where("active", "==", true));
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAccounts(list);
      } catch (err) {
        console.error("Failed to load accounts", err);
      }
    };

    // You can call it once on mount, or only when needed
    fetchAccounts();
  }, []);

  useEffect(() => {
    const fetchHotelAccounts = async () => {
      try {
        const q = query(collection(db, "hotelAccounts"), where("active", "==", true));
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setHotelAccounts(list);
      } catch (err) {
        console.error("Failed to load hotel accounts", err);
      }
    };

    // You can call it once on mount, or only when needed
    fetchHotelAccounts();
  }, []);

  // Fetch Batches
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const q = query(collection(db, "batches"), where("active", "==", true));
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setBatches(list.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch (err) {
        console.error("Failed to load batches", err);
      }
    };

    fetchBatches();
  }, []);

  const isAlmostDueForCheckIn = (booking) => {
    if (booking.status !== 'pending') return false;
    
    const now = new Date();
    const checkIn = booking.checkIn?.toDate?.() || new Date(booking.checkIn);
    
    if (checkIn < now) return false; // already past
    
    const hoursUntil = (checkIn - now) / (1000 * 60 * 60);
    
    return hoursUntil <= TIME_WINDOWS.checkInWarningHours;
  };

  const isUrgentCheckIn = (booking) => {
    if (!isAlmostDueForCheckIn(booking)) return false;
    const now = new Date();
    const checkIn = booking.checkIn?.toDate?.() || new Date(booking.checkIn);
    const hoursUntil = (checkIn - now) / (1000 * 60 * 60);
    return hoursUntil <= TIME_WINDOWS.checkInUrgentHours;
  };

  const isAlmostDueForCheckOut = (booking) => {
    if (booking.status !== 'checked-in') return false;
    
    const now = new Date();
    const checkOut = booking.checkOut?.toDate?.() || new Date(booking.checkOut);
    
    if (checkOut < now) return false;
    
    const hoursUntil = (checkOut - now) / (1000 * 60 * 60);
    return hoursUntil <= TIME_WINDOWS.checkOutWarningHours;
  };

  const isUrgentCheckOut = (booking) => {
    if (!isAlmostDueForCheckOut(booking)) return false;
    const now = new Date();
    const checkOut = booking.checkOut?.toDate?.() || new Date(booking.checkOut);
    const hoursUntil = (checkOut - now) / (1000 * 60 * 60);
    return hoursUntil <= TIME_WINDOWS.checkOutUrgentHours;
  };

  useEffect(() => {
    if (user && userData) {
      fetchRoomTypes();
      fetchRatePlans();
      fetchAmenities();
    }
  }, [user, userData]);

  useEffect(() => {
    if (user && userData && roomTypes.length > 0 && ratePlans.length > 0) {
      fetchRooms();
    }
  }, [user, userData, roomTypes, ratePlans]);

  useEffect(() => {
    if (user && userData && rooms.length > 0 && roomTypes.length > 0 && ratePlans.length > 0 && amenities.length > 0) {
      const q = query(bookingsCollectionRef, orderBy("createdAt", "desc"));
      const unsub = onSnapshot(q, (snapshot) => {
        const bookingsList = snapshot.docs.map(docSnapshot => {
          const data = docSnapshot.data();
          const room = rooms.find(r => r.id === data.roomId);
          const roomType = roomTypes.find(rt => rt.id === data.roomTypeId);

          return {
            id: docSnapshot.id,
            ...data,
            roomNumber: room?.roomNumber || 'Unknown',
            roomTypeName: roomType?.name || data.roomTypeId,
            checkInDate: data.checkIn?.toDate ? data.checkIn.toDate() : new Date(),
            checkOutDate: data.checkOut?.toDate ? data.checkOut.toDate() : new Date()
          };
        });

        setBookings(bookingsList);
      });

      return () => unsub();
    }
  }, [user, userData, rooms, roomTypes, ratePlans, amenities]);
  
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredBookings([]);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filtered = bookings.filter(booking =>
      booking.guestName.toLowerCase().includes(lowerSearch) ||
      booking.guestPhone.includes(searchTerm) ||
      booking.roomNumber.toLowerCase().includes(lowerSearch) ||
      (booking.roomTypeName && booking.roomTypeName.toLowerCase().includes(lowerSearch))
    );

    setFilteredBookings(filtered);
  }, [searchTerm, bookings]);

  const fetchRoomTypes = async () => {
    try {
      const q = query(roomTypesCollectionRef, where("active", "==", true));
      const snapshot = await getDocs(q);
      const types = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRoomTypes(types);
    } catch (err) {
      console.error("Error fetching room types:", err);
    }
  };

  useEffect(() => {
    if (!bookings.length) return;

    const almostIn  = bookings.filter(isAlmostDueForCheckIn);
    const almostOut = bookings.filter(isAlmostDueForCheckOut);
    
    // Get all three cases
    const overdueIn  = bookings.filter(isOverdueForCheckIn);
    const expired    = bookings.filter(isExpiredBooking);
    const overdueOut = bookings.filter(isOverdueForCheckOut);

    setAlmostDueCheckIns(almostIn);
    setAlmostDueCheckOuts(almostOut);
    
    // Set all three cases
    setOverdueCheckIns(overdueIn);
    setExpiredBookings(expired);
    setOverdueCheckOuts(overdueOut);

    // Optional: Show overdue popup automatically if there are any overdue
    if (overdueIn.length > 0 || overdueOut.length > 0 || expired.length > 0) {
      // You can automatically show the popup if you want
      // setShowOverduePopup(true);
    }

    // Optional: browser notification
    if (almostIn.length + almostOut.length + overdueIn.length + overdueOut.length + expired.length > 0) {
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("Action Required", {
          body: `${overdueIn.length} overdue check-in${overdueIn.length !== 1 ? 's' : ''}, 
                  ${overdueOut.length} overdue check-out${overdueOut.length !== 1 ? 's' : ''},
                  ${expired.length} expired booking${expired.length !== 1 ? 's' : ''},
                  ${almostIn.length} upcoming check-in${almostIn.length !== 1 ? 's' : ''} & 
                  ${almostOut.length} upcoming check-out${almostOut.length !== 1 ? 's' : ''}`,
          icon: "/hotel-icon.png"
        });
      }
    }
  }, [bookings]);

  const fetchRooms = async () => {
    try {
      const q = query(roomsCollectionRef, where("active", "==", true));
      const snapshot = await getDocs(q);
      const roomsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const enrichedRooms = roomsList.map(room => {
        const roomType = roomTypes.find(rt => rt.id === room.roomTypeId);
        return {
          ...room,
          roomTypeName: roomType?.name || 'Unknown'
        };
      });
      setRooms(enrichedRooms);
    } catch (err) {
      console.error("Error fetching rooms:", err);
    }
  };

  const fetchRatePlans = async () => {
    try {
      const q = query(ratePlansCollectionRef, where("active", "==", true));
      const snapshot = await getDocs(q);
      const plans = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRatePlans(plans);
    } catch (err) {
      console.error("Error fetching rate plans:", err);
    }
  };

  const fetchAmenities = async () => {
    try {
      const snapshot = await getDocs(collection(db, "amenities"));
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAmenities(list.filter(a => a.active !== false));
    } catch (err) {
      console.error("Error fetching amenities:", err);
    }
  };

  const checkRoomAvailability = async (roomId, checkIn, checkOut) => {
    if (!roomId || !checkIn || !checkOut) return false;
    
    try {
      // Convert checkIn and checkOut to Firestore timestamps
      const checkInTimestamp = checkIn?.toDate ? checkIn : Timestamp.fromDate(new Date(checkIn));
      const checkOutTimestamp = checkOut?.toDate ? checkOut : Timestamp.fromDate(new Date(checkOut));
      
      // Query for bookings that overlap with the selected dates
      const bookingsQuery = query(
        collection(db, "bookings"),
        where("roomId", "==", roomId)
      );
      const snapshot = await getDocs(bookingsQuery);
      
      for (const doc of snapshot.docs) {
        const booking = doc.data();
        
        // Skip cancelled and checked-out bookings
        if (booking.status === 'cancelled' || booking.status === 'checked-out') continue;
        
        // Check if there's an overlap
        if (checkInTimestamp.toMillis() < booking.checkOut.toMillis() && 
            checkOutTimestamp.toMillis() > booking.checkIn.toMillis()) {
          
          // If it's the same booking we're editing, allow it
          if (doc.id === selectedBooking?.id) continue;
          
          return false; // Room is not available
        }
      }
      
      return true; // Room is available
    } catch (err) {
      console.error("Error checking room availability:", err);
      return false;
    }
  };

  const fetchAvailableRoomsForEdit = async (checkIn, checkOut, roomTypeId = null) => {
    if (!checkIn || !checkOut) {
      setAvailableEditRooms([]);
      return;
    }

    setCheckingAvailability(true);
    try {
      // Convert to timestamps
      const checkInTimestamp = checkIn?.toDate ? checkIn : Timestamp.fromDate(new Date(checkIn));
      const checkOutTimestamp = checkOut?.toDate ? checkOut : Timestamp.fromDate(new Date(checkOut));
      
      // Get all active rooms
      const roomsQuery = query(roomsCollectionRef, where("active", "==", true));
      const roomsSnapshot = await getDocs(roomsQuery);
      const allRooms = roomsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Filter by room type if specified
      let filteredRooms = allRooms;
      if (roomTypeId) {
        filteredRooms = allRooms.filter(room => room.roomTypeId === roomTypeId);
      }
      
      // Check availability for each room
      const availableRooms = [];
      
      for (const room of filteredRooms) {
        // Skip rooms that are out of order
        if (room.status === 'Out-of-Order') continue;
        
        // Get all bookings for this room
        const bookingsQuery = query(
          collection(db, "bookings"),
          where("roomId", "==", room.id)
        );
        const bookingsSnapshot = await getDocs(bookingsQuery);
        
        let isAvailable = true;
        
        for (const doc of bookingsSnapshot.docs) {
          const booking = doc.data();
          
          // Skip cancelled and checked-out bookings
          if (booking.status === 'cancelled' || booking.status === 'checked-out') continue;
          
          // Check for overlap
          if (checkInTimestamp.toMillis() < booking.checkOut.toMillis() && 
              checkOutTimestamp.toMillis() > booking.checkIn.toMillis()) {
            
            // If it's the booking we're editing, allow it
            if (doc.id === selectedBooking?.id) continue;
            
            isAvailable = false;
            break;
          }
        }
        
        if (isAvailable) {
          const roomType = roomTypes.find(rt => rt.id === room.roomTypeId);
          availableRooms.push({
            ...room,
            roomTypeName: roomType?.name || 'Unknown'
          });
        }
      }
      
      setAvailableEditRooms(availableRooms);
    } catch (err) {
      console.error("Error fetching available rooms:", err);
      setError("Failed to check room availability");
      setShowModal(true);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;
    
    const inDate  = checkIn?.toDate  ? checkIn.toDate()  : new Date(checkIn);
    const outDate = checkOut?.toDate ? checkOut.toDate() : new Date(checkOut);
    
    const diffMs = outDate - inDate;
    if (diffMs <= 0) return 0;
    
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24)); // Match Booking component's calculation
  };

  const calculateNightlyRate = (roomId, ratePlanId, selectedAmenities = []) => {
    if (!roomId || !ratePlanId) return 0;

    const room = rooms.find(r => r.id === roomId);
    if (!room) return 0;

    const roomType = roomTypes.find(rt => rt.id === room.roomTypeId);
    if (!roomType?.baseRate) return 0;

    let rate = roomType.baseRate;

    const ratePlan = ratePlans.find(rp => rp.id === ratePlanId);
    if (ratePlan?.rateAdjustment) {
      rate = rate * (1 + ratePlan.rateAdjustment / 100);
    }

    return Math.round(rate);
  };

  const calculateTotalPrice = (booking, newCheckIn, newCheckOut, newSelectedAmenities, newRoomId, newRatePlanId) => {
    if (!booking) return { total: 0, nights: 0 };

    const nights = calculateNights(newCheckIn || booking.checkIn, newCheckOut || booking.checkOut);
    if (nights <= 0) return { total: 0, nights: 0 };

    // Calculate new nightly rate based on room and rate plan
    const newNightlyRate = calculateNightlyRate(
      newRoomId || booking.roomId,
      newRatePlanId || booking.ratePlanId,
      newSelectedAmenities || booking.selectedAmenities || []
    );

    const baseTotal = newNightlyRate * nights;

    // Add extra amenities (assuming selectedAmenities are extras only)
    let amenitiesTotal = 0;
    (newSelectedAmenities || booking.selectedAmenities || []).forEach(id => {
      const amenity = amenities.find(a => a.id === id);
      if (amenity?.price) {
        amenitiesTotal += amenity.price * nights;
      }
    });

    const total = Math.round(baseTotal + amenitiesTotal);
    return { total, nights, nightlyRate: newNightlyRate };
  };

  useEffect(() => {
    if (!isEditModalOpen || !selectedBooking) return;

    const result = calculateTotalPrice(
      selectedBooking,
      editForm.checkIn,
      editForm.checkOut,
      editForm.selectedAmenities,
      editForm.roomId,
      editForm.ratePlanId
    );

    setEditForm(prev => ({ 
      ...prev, 
      totalPrice: result.total,
      nights: result.nights,
      nightlyRate: result.nightlyRate
    }));
  }, [
    isEditModalOpen,
    editForm.checkIn,
    editForm.checkOut,
    editForm.selectedAmenities,
    editForm.roomId,
    editForm.ratePlanId,
    selectedBooking,
    rooms,
    roomTypes,
    ratePlans,
    amenities
  ]);

  const extraAmenitiesCost = () => {
    if (!editForm.checkIn || !editForm.checkOut) return 0;
    const nights = calculateNights(editForm.checkIn, editForm.checkOut);
    return editForm.selectedAmenities.reduce((sum, id) => {
      const am = amenities.find(a => a.id === id);
      return sum + (am?.price || 0) * nights;
    }, 0);
  };

  const downloadGuestBill = (booking) => {
    const enriched = {
      ...booking,
      amenities,
    };
    GuestBillPDF.download(enriched);
  };

  // Case 1: Check-in time has passed, check-out time NOT reached, user not checked in
  const isOverdueForCheckIn = (booking) => {
    if (booking.status !== 'pending') return false;
    
    const now = new Date();
    const checkIn = booking.checkIn?.toDate?.() || new Date(booking.checkIn);
    const checkOut = booking.checkOut?.toDate?.() || new Date(booking.checkOut);
    
    // Check-in time has passed AND checkout time NOT reached
    return checkIn < now && now < checkOut;
  };

  // Case 2: BOTH check-in AND check-out times have passed, user not checked in (Expired)
  const isExpiredBooking = (booking) => {
    if (booking.status !== 'pending') return false;
    
    const now = new Date();
    const checkIn = booking.checkIn?.toDate?.() || new Date(booking.checkIn);
    const checkOut = booking.checkOut?.toDate?.() || new Date(booking.checkOut);
    
    // Both check-in AND check-out times have passed
    return checkIn < now && checkOut < now;
  };

  // Case 3: User was checked in, check-out time has passed, user not checked out
  const isOverdueForCheckOut = (booking) => {
    if (booking.status !== 'checked-in') return false;
    
    const now = new Date();
    const checkOut = booking.checkOut?.toDate?.() || new Date(booking.checkOut);
    
    // Check-out time has passed
    return checkOut < now;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffb267';
      case 'checked-in': return '#2196F3';
      case 'checked-out': return '#9C27B0';
      case 'cancelled': return '#fc796f';
      default: return '#757575';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'checked-in': return 'Checked In';
      case 'checked-out': return 'Checked Out';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const openEditModal = (booking) => {
    setSelectedBooking(booking);

    setAvailableEditRooms([]);
    setCheckingAvailability(false);

    const result = calculateTotalPrice(
      booking,
      booking.checkIn,
      booking.checkOut,
      booking.selectedAmenities || []
    );
    
    setEditForm({
      guestName: booking.guestName || '',
      guestPhone: booking.guestPhone || '',
      guestEmail: booking.guestEmail || '',
      checkIn: booking.checkIn,
      checkOut: booking.checkOut,
      roomId: booking.roomId || '',
      ratePlanId: booking.ratePlanId || '',
      totalPrice: result.total || booking.totalPrice || 0,
      nights: result.nights || booking.nights || 0,
      deposit: booking.deposit || 0,
      notes: booking.notes || '',
      selectedAmenities: booking.selectedAmenities || []
    });
    setIsEditModalOpen(true);
  };

  const handleEditBooking = async () => {
    if (!selectedBooking?.id) return;

    // Check if the selected room is available (if it changed)
    if (editForm.roomId !== selectedBooking.roomId && editForm.checkIn && editForm.checkOut) {
      const isAvailable = await checkRoomAvailability(
        editForm.roomId,
        editForm.checkIn,
        editForm.checkOut
      );
      
      if (!isAvailable) {
        const room = rooms.find(r => r.id === editForm.roomId);
        setError(`Room ${room?.roomNumber || editForm.roomId} is not available for the selected dates`);
        setShowModal(true);
        return;
      }
    }

    setUpdate(true);
    try {
      const bookingDoc = doc(db, "bookings", selectedBooking.id);
      
      // Calculate final result
      const result = calculateTotalPrice(
        selectedBooking,
        editForm.checkIn,
        editForm.checkOut,
        editForm.selectedAmenities,
        editForm.roomId,
        editForm.ratePlanId
      );
      
      await updateDoc(bookingDoc, {
        guestName: editForm.guestName.trim(),
        guestPhone: editForm.guestPhone.trim(),
        guestEmail: editForm.guestEmail.trim(),
        checkIn: editForm.checkIn,
        checkOut: editForm.checkOut,
        roomId: editForm.roomId,
        ratePlanId: editForm.ratePlanId,
        deposit: editForm.deposit,
        notes: editForm.notes.trim(),
        selectedAmenities: editForm.selectedAmenities,
        updatedAt: Timestamp.now(),
        totalPrice: result.total,
        nights: result.nights,
        nightlyRate: result.nightlyRate,
        balance: result.total - (editForm.deposit || selectedBooking.deposit || 0),
      });

      setSuccessMessage("Booking updated successfully");
      setIsEditModalOpen(false);
    } catch (err) {
      console.error("Error updating booking:", err);
      setError("Failed to update booking");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const getExtraAmenitiesForEdit = () => {
    if (!editForm.roomId) return [];
    const room = rooms.find(r => r.id === editForm.roomId);
    const roomType = roomTypes.find(rt => rt.id === room?.roomTypeId);
    const includedIds = roomType?.amenityIds || [];
    return amenities.filter(a => !includedIds.includes(a.id));
  };

  const handleCheckIn = async () => {
    // if (!selectedBooking?.id) return;
    if (!selectedBooking?.id) return;

    const now = new Date();
    const checkInDate = selectedBooking.checkIn?.toDate ? selectedBooking.checkIn.toDate() : new Date(selectedBooking.checkIn);

    // Allow check-in on the check-in day (even a few hours early is usually okay)
    // But block if trying to check in clearly before the day
    const checkInDayStart = new Date(checkInDate);
    checkInDayStart.setHours(0, 0, 0, 0);

    if (now < checkInDayStart) {
      setError(`Cannot check in before the reserved check-in date (${formatDateTime(checkInDate)})`);
      setShowModal(true);
      return;
    }

    setUpdate(true);
    try {
      const bookingDoc = doc(db, "bookings", selectedBooking.id);
      const roomDoc = doc(db, "rooms", selectedBooking.roomId);
      
      // Get user details
      const userFullName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'Staff';
      const userPhone = userData?.phone || 'N/A';
      
      const now = Timestamp.now();
      
      // Update booking
      await updateDoc(bookingDoc, {
        status: 'checked-in',
        checkInActual: now,
        checkedInBy: {
          name: userFullName,
          phone: userPhone,
          userId: user?.uid || 'N/A'
        },
        checkedInAt: now,
        updatedAt: now
      });

      // Update room status to Occupied
      await updateDoc(roomDoc, {
        status: 'Occupied',
        updatedAt: now
      });

      await addDoc(collection(db, "bookings", selectedBooking.id, "activities"), {
        type: 'check_in',
        guestName: selectedBooking.guestName,
        roomNumber: selectedBooking.roomNumber,
        performedBy: userFullName,
        performedById: user?.uid || 'N/A',
        timestamp: now,
        details: {
          roomType: selectedBooking.roomTypeName,
          checkInDate: selectedBooking.checkInDate,
          checkOutDate: selectedBooking.checkOutDate,
          staffPhone: userPhone
        }
      });

      setSuccessMessage(`${selectedBooking.guestName} has been checked in successfully by ${userFullName}`);
      setIsCheckInModalOpen(false);
    } catch (err) {
      console.error("Error checking in:", err);
      setError("Failed to check in guest");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };


  const handleCheckOut = async () => {
    if (!selectedBooking?.id) return;

    const totalDue = (selectedBooking.totalPrice || 0) + (incidentalTotal || 0);
    const paid = selectedBooking.deposit || 0;
    const currentBalance = totalDue - paid;

    if (currentBalance <= 0) {
      // Normal checkout - no balance
      proceedWithCheckOut();
    } else {
      // Show settle modal instead
      setSelectedAccountId('');           // reset
      setSelectedBatchId('');
      setTransferMemo('Balance transferred to account at checkout');
      setIsSettleToAccountModalOpen(true);
    }
  };

  const proceedWithCheckOut = async () => {
    setUpdate(true);
    try {
      const bookingDoc = doc(db, "bookings", selectedBooking.id);
      const roomDoc = doc(db, "rooms", selectedBooking.roomId);

      const userFullName = userData
        ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Staff'
        : 'Staff';
      const userPhone = userData?.phone || 'N/A';

      const now = Timestamp.now();

      await updateDoc(bookingDoc, {
        status: 'checked-out',
        checkOutActual: now,
        checkedOutBy: {
          name: userFullName,
          phone: userPhone,
          userId: user?.uid || 'N/A'
        },
        checkedOutAt: now,
        updatedAt: now,
        balance: 0   // ensure cleared
      });

      await updateDoc(roomDoc, {
        status: 'Out-of-Order',
        updatedAt: now
      });

      // activity log (your existing code)
      await addDoc(collection(db, "bookings", selectedBooking.id, "activities"), {
        type: 'check_out',
        guestName: selectedBooking.guestName,
        roomNumber: selectedBooking.roomNumber,
        performedBy: userFullName,
        performedById: user?.uid || 'N/A',
        timestamp: now,
        details: {
          roomType: selectedBooking.roomTypeName,
          checkInDate: selectedBooking.checkInDate,
          checkOutDate: selectedBooking.checkOutDate,
          totalNights: selectedBooking.nights || 0,
          totalPrice: selectedBooking.totalPrice || 0,
          deposit: selectedBooking.deposit || 0,
          balance: 0,
          staffPhone: userPhone,
          settlementMethod: 'full payment'
        }
      });

      setSuccessMessage(`${selectedBooking.guestName} has been checked out successfully`);
      setIsCheckOutModalOpen(false);
    } catch (err) {
      console.error("Checkout error:", err);
      setError("Failed to check out guest");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleTransferToAccountAndCheckout = async () => {
    if (!selectedBooking?.id || !selectedAccountId) {
      setError("Please select an account");
      setShowModal(true);
      return;
    }

    // Safety guard - ensure we have the guest name
    const guestName = selectedBooking.guestName || "Unknown Guest";

    setUpdate(true);
    try {
      const bookingRef = doc(db, "bookings", selectedBooking.id);
      const accountRef = doc(db, "accounts", selectedAccountId);
      const now = Timestamp.now();
      const userFullName = userData
        ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Staff'
        : 'Staff';
      const userPhone = userData?.phone || 'N/A';

      const totalRoomCharges = selectedBooking.totalPrice || 0;
      const totalIncidental = incidentalTotal || 0;
      const totalDue = totalRoomCharges + totalIncidental;
      const totalPaid = selectedBooking.deposit || 0;
      const balanceTransferred = totalDue - totalPaid;

      if (balanceTransferred <= 0) {
        setError("No balance to transfer");
        setShowModal(true);
        return;
      }

      const checkInDate = selectedBooking.checkIn?.toDate?.()?.toISOString().split('T')[0] || '—';
      const checkOutDate = selectedBooking.checkOut?.toDate?.()?.toISOString().split('T')[0] || '—';

      const detailedDescription = [
        `Checkout transfer — Guest: ${guestName}`,
        `Room: ${selectedBooking.roomNumber || '—'} (${selectedBooking.roomTypeName || '—'})`,
        `Period: ${checkInDate} → ${checkOutDate} (${selectedBooking.nights || '?'} nights)`,
        transferMemo.trim() ? `Memo: ${transferMemo.trim()}` : null,
      ].filter(Boolean).join(" | ");

      const selectedBatch = batches.find(b => b.id === selectedBatchId);

      // Create checkout entry
      const checkoutRef = await addDoc(collection(db, "checkoutEntries"), {
        date: new Date().toISOString().split('T')[0],
        description: detailedDescription,
        booking: {
          id: selectedBooking.id,
          guestName: guestName,                    // ← safe
          guestPhone: selectedBooking.guestPhone || '',
          guestEmail: selectedBooking.guestEmail || '',
          roomId: selectedBooking.roomId,
          roomNumber: selectedBooking.roomNumber || '',
          roomTypeName: selectedBooking.roomTypeName || '',
          nights: selectedBooking.nights || 0,
          checkIn: selectedBooking.checkIn,
          checkOut: selectedBooking.checkOut,
          checkInActual:    selectedBooking.checkInActual,
          checkOutActual:   now,
          totalPrice: totalRoomCharges,
          deposit: totalPaid,
          incidentalTotal: totalIncidental,
          balanceAtCheckout: balanceTransferred,
          batchId: selectedBatchId || null,
          batchName: selectedBatch?.name || "No Batch",
        },
        // Accounting lines
        lines: [
          {
            accountId:   selectedAccountId,
            accountName: accounts.find(a => a.id === selectedAccountId)?.name || "Unknown Account",
            debit:       balanceTransferred,
            credit:      0,
            memo:        "Receivable → Company/Guest Account"
          },
          {
            accountId:   null, // or use a fixed "Accounts Receivable - Guests" account ID if you have one
            accountName: "Accounts Receivable - Guests",
            debit:       0,
            credit:      balanceTransferred,
            memo:        "Clear guest balance at checkout"
          }
        ],
        type: "checkout_balance_transfer",
        amount: balanceTransferred,
        currency: "NGN",
        userName: userFullName,
        userId: user?.uid,
        memo: transferMemo.trim() || "Balance transferred to account at checkout",
        createdAt: now,
        bookingId: selectedBooking.id,
        checkoutId: ""
      });

      const checkoutId = checkoutRef.id.substring(0, 8);
      await updateDoc(checkoutRef, { checkoutId });

      // Update booking
      await updateDoc(bookingRef, {
        status: 'checked-out',
        checkOutActual: now,
        checkedOutBy: { name: userFullName, phone: userPhone, userId: user?.uid || 'N/A' },
        checkedOutAt: now,
        balance: 0,
        settledToAccountId: selectedAccountId,
        settledToBatchId: selectedBatchId || null,
        settledToBatchName: selectedBatch?.name || "No Batch",
        settledAt: now,
        updatedAt: now
      });

      // Update account balance
      const accountSnap = await getDoc(accountRef);
      const currentData = accountSnap.data() || {};
      const currentBalance = currentData.currentBalance || 0;
      await updateDoc(accountRef, {
        currentBalance: currentBalance + balanceTransferred,
        lastActivity: now,
        updatedAt: now
      });

      // Room status
      await updateDoc(doc(db, "rooms", selectedBooking.roomId), {
        status: 'Out-of-Order',
        updatedAt: now
      });

      // Activity log (safe guestName)
      await addDoc(collection(db, "bookings", selectedBooking.id, "activities"), {
        type: 'check_out_account_transfer',
        amountTransferred: balanceTransferred,
        toAccountId: selectedAccountId,
        toAccountName: accounts.find(a => a.id === selectedAccountId)?.name || 'Unknown',
        memo: transferMemo.trim() || 'Balance transferred at checkout',
        performedBy: userFullName,
        performedById: user?.uid || 'N/A',
        timestamp: now,
        totalRoomCharges,
        incidentalTotal: totalIncidental,
        totalDue,
        totalPaid,
        roomNumber: selectedBooking.roomNumber,
        roomType: selectedBooking.roomTypeName,
        nights: selectedBooking.nights || 0,
        guestName: guestName   // ← safe
      });

      setSuccessMessage(`₦${balanceTransferred.toLocaleString()} transferred to ${accounts.find(a => a.id === selectedAccountId)?.name || 'Unknown'}. Guest ${guestName} checked out.`);

      setIsSettleToAccountModalOpen(false);
      setIsCheckOutModalOpen(false);
      setShowModal(true);

    } catch (err) {
      setError(`Failed to process transfer and checkout: ${err.message || err.toString()}`);
      setShowModal(true);
    } finally {
      setUpdate(false);
    }
  };

  const handleVoidPayment = async () => {
    if (!paymentToVoid || !selectedBooking?.id) return;

    setUpdate(true);
    try {
      await runTransaction(db, async (transaction) => {
        // 1. Get current booking document
        const bookingRef = doc(db, "bookings", selectedBooking.id);
        const bookingSnap = await transaction.get(bookingRef);
        if (!bookingSnap.exists()) {
          throw new Error("Booking not found");
        }
        const bookingData = bookingSnap.data();

        // 2. Get current payment document
        const paymentRef = doc(db, "payments", paymentToVoid.id);
        const paymentSnap = await transaction.get(paymentRef);
        if (!paymentSnap.exists()) {
          throw new Error("Payment not found");
        }
        const paymentData = paymentSnap.data();

        // Prevent double-voiding
        if (paymentData.voided) {
          throw new Error("This payment is already voided");
        }

        const voidAmount = paymentData.amount || 0;

        // 3. Calculate new deposit & balance
        const currentDeposit = bookingData.deposit || 0;
        const newDeposit = currentDeposit - voidAmount;
        const newBalance = (bookingData.totalPrice || 0) - newDeposit;

        // Safety check: don't allow negative deposit
        if (newDeposit < 0) {
          throw new Error("Cannot void — deposit would go negative");
        }

        // 4. Update booking (deposit & balance)
        transaction.update(bookingRef, {
          deposit: newDeposit,
          balance: newBalance,
          updatedAt: Timestamp.now()
        });

        // 5. Mark payment as voided
        transaction.update(paymentRef, {
          voided: true,
          voidedAt: Timestamp.now(),
          voidedBy: {
            name: userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'Staff',
            userId: user?.uid || 'N/A'
          },
          updatedAt: Timestamp.now()
        });
      });

      // 6. Log the void action in activities
      const userFullName = userData
        ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Staff'
        : 'Staff';

      await addDoc(collection(db, "bookings", selectedBooking.id, "activities"), {
        type: 'payment_voided',
        originalPaymentId: paymentToVoid.id,
        amountVoided: paymentToVoid.amount,
        method: paymentToVoid.paymentMethod,
        performedBy: userFullName,
        performedById: user?.uid || 'N/A',
        timestamp: Timestamp.now(),
        notes: `Voided payment of ₦${paymentToVoid.amount.toLocaleString()}. Deposit reduced.`
      });

      if (paymentToVoid.journalDocId) {
        try {
            await deleteDoc(doc(db, "journalEntries", paymentToVoid.journalDocId));
            // console.log("Journal entry deleted successfully");
        } catch (delErr) {
            // console.warn("Failed to delete journal entry:", delErr.message);
        }
      } 
        // Fallback: try by journalId (short ID) if full ID not stored
      else if (paymentToVoid.journalId) {
        // You would need to query here, but it's less reliable — better to store journalDocId
        // console.warn("journalDocId not found, skipping journal deletion");
      }

      // 7. Refresh UI
      await fetchPaymentHistory(selectedBooking);

      setSuccessMessage(`Payment of ₦${paymentToVoid.amount.toLocaleString()} voided and deposit adjusted.`);
      setIsVoidConfirmModalOpen(false);
      setPaymentToVoid(null);
    } catch (err) {
      console.error("Void transaction failed:", err);
      setError(err.message || "Failed to void payment and adjust deposit");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleCancelBookingConfirm = async () => {
    if (!selectedBooking?.id) return;

    setUpdate(true);
    try {
      const bookingDoc = doc(db, "bookings", selectedBooking.id);
      const roomDoc = doc(db, "rooms", selectedBooking.roomId);
      
      const userFullName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'Staff';
      const userPhone = userData?.phone || 'N/A';
      
      const now = Timestamp.now();
      const finalReason = cancelReason.trim() || 'N/A';

      await updateDoc(bookingDoc, {
        status: 'cancelled',
        cancellationReason: finalReason,     // ← new field
        cancelledBy: {
          name: userFullName,
          phone: userPhone,
          userId: user?.uid || 'N/A'
        },
        cancelledAt: now,
        updatedAt: now
      });

      await updateDoc(roomDoc, {
        status: 'Available',
        updatedAt: now
      });

      await addDoc(collection(db, "bookings", selectedBooking.id, "activities"), {
        type: 'cancellation',
        guestName: selectedBooking.guestName,
        roomNumber: selectedBooking.roomNumber,
        performedBy: userFullName,
        performedById: user?.uid || 'N/A',
        timestamp: now,
        details: {
          roomType: selectedBooking.roomTypeName,
          checkInDate: selectedBooking.checkInDate,
          checkOutDate: selectedBooking.checkOutDate,
          totalNights: selectedBooking.nights || 0,
          totalPrice: selectedBooking.totalPrice || 0,
          deposit: selectedBooking.deposit || 0,
          reason: finalReason,
          staffPhone: userPhone
        }
      });

      setSuccessMessage(`Booking for ${selectedBooking.guestName} has been cancelled (${finalReason})`);
      setIsCancelConfirmModalOpen(false);
      setCancelReason('');
    } catch (err) {
      console.error("Error cancelling booking:", err);
      setError("Failed to cancel booking");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const downloadReservation = (booking) => {
    const enrichedBooking = {
      ...booking,
      amenities: amenities
    };
    ReservationPDF.downloadPDF(enrichedBooking);
  };

  // Record Payment – includes cashier name
  const handleRecordPayment = async () => {
    if (!selectedBooking?.id || !paymentAmount || Number(paymentAmount) <= 0) {
      setError("Please enter a valid payment amount");
      setShowModal(true);
      return;
    }

    if (!selectedPaymentAccountId) {
      setError("Please select a payment account");
      setShowModal(true);
      return;
    }

    const amount = Number(paymentAmount);
    const selectedAccount = hotelAccounts.find(a => a.id === selectedPaymentAccountId);

    if (!selectedAccount) {
      setError("Selected account not found");
      setShowModal(true);
      return;
    }

    setUpdate(true);

    try {
      const cashierName = userData
        ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Staff'
        : 'Staff';

      const bookingRef = doc(db, "bookings", selectedBooking.id);
      const currentDeposit = selectedBooking.deposit || 0;
      const newDeposit = currentDeposit + amount;
      const newBalance = (selectedBooking.totalPrice || 0) + (incidentalTotal || 0) - newDeposit;

      // 1. Update booking deposit & balance
      await updateDoc(bookingRef, {
        deposit: newDeposit,
        balance: newBalance,
        updatedAt: Timestamp.now()
      });

      // 2. Activity log
      await addDoc(collection(db, "bookings", selectedBooking.id, "activities"), {
        type: 'payment',
        amount,
        method: selectedAccount.name || 'Account Payment',
        payerName: payerName.trim() || selectedBooking.guestName,
        notes: paymentNotes.trim(),
        cashierName,
        performedBy: cashierName,
        performedById: user?.uid || 'N/A',
        timestamp: Timestamp.now(),
        paymentAccountId: selectedPaymentAccountId,
        paymentAccountName: selectedAccount.name
      });      

      // 4. Create REAL Journal Entry using hotelAccounts collection
      const journalEntriesRef = collection(db, "journalEntries");   // ← matches your JournalEntries component

      const description = `Guest Payment - ${selectedBooking.guestName} (Room ${selectedBooking.roomNumber || '—'})`;

      const journalData = {
        date: new Date().toISOString().split('T')[0],
        description,
        lines: [
          {
            accountName: selectedAccount.name,     // Debit: Cash / Bank / POS increases
            debit: amount,
            credit: 0
          },
          {
            accountName: "Accounts Receivable - Guests",   // Credit: Reduces what guest owes
            debit: 0,
            credit: amount
          }
        ],
        userId: user?.uid,
        userName: cashierName,
        bookingId: selectedBooking.id,
        bookingReference: selectedBooking.roomNumber || '',
        type: "guest_payment",
        createdAt: Timestamp.now(),
        journalId: ""   // ← will be auto-generated by your JournalEntries logic
      };

      const docRef = await addDoc(journalEntriesRef, journalData);

      // Optional: Generate journalId like in your JournalEntries component
      const journalId = docRef.id.substring(0, 8);
      await updateDoc(docRef, { journalId });

      // 3. Optional: payments collection (you can keep or remove)
      await addDoc(collection(db, "payments"), {
        bookingId: selectedBooking.id,
        guestName: selectedBooking.guestName,
        amount,
        payerName: payerName.trim() || selectedBooking.guestName,
        paymentMethod: selectedAccount.name,
        notes: paymentNotes.trim(),
        cashierName,
        timestamp: Timestamp.now(),
        paymentAccountId: selectedPaymentAccountId,
        journalDocId: docRef.id,
        journalId: journalId
      });

      setSuccessMessage(`Payment of ₦${amount.toLocaleString()} recorded successfully`);

      // Reset form
      setIsPaymentModalOpen(false);
      setPaymentAmount('');
      setPayerName('');
      setPaymentNotes('');
      setSelectedPaymentAccountId('');

    } catch (err) {
      setError(`Failed to record payment: ${err.message}`);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  // Fetch Payment History – uses only 'where' clause, sorts client-side (no index needed)
  const fetchPaymentHistory = async (booking) => {
    if (!booking?.id) return;

    setFetchingHistory(true);
    setPaymentHistory([]);
    try {
      const paymentsRef = collection(db, "payments");
      const q = query(paymentsRef, where("bookingId", "==", booking.id));
      const snapshot = await getDocs(q);

      let history = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));

      // Sort newest first on client side
      history.sort((a, b) => b.timestamp - a.timestamp);

      setPaymentHistory(history);
    } catch (err) {
      console.error("Error fetching payment history:", err);
      setError("Failed to load payment history");
      setShowModal(true);
    } finally {
      setFetchingHistory(false);
    }
  };

  const allowedRoles = ['Admin', 'Supreme', 'Cashier', 'Front Desk'];

  if (!userData || !userData.status) {
    return <Loader text="Loading" />;
  }

  if (!allowedRoles.includes(userData.status)) {
    return <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>;
  }

  return (
    <>
      {update && <Loader text="Processing" />}

      <div className="dashboard-container padding-bottom-d">
        <div className="pos-dashboard reservation-dashboard" style={{ margin: 'auto' }}>
          <div className="column reservation-left-block">
            <input
              type="text"
              className="input-field1 reservation-search-field"
              placeholder="Search by guest name, phone, room number, or room type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div>
              {(almostDueCheckIns.length > 0 || almostDueCheckOuts.length > 0 || 
                overdueCheckIns.length > 0 || overdueCheckOuts.length > 0 || expiredBookings.length > 0) && (
                <div
                  className="reservation-notice column"                 
                  onClick={() => setShowOverduePopup(true)}                  
                >
                  <div style={{ textAlign: 'left' }}>
                    <i className="fas fa-bell" style={{ marginRight: '8px' }}></i>
                    <strong>Attention Required!</strong>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {overdueCheckIns.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="badge" style={{ 
                          background: '#dc3545',
                          padding: '8px',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}>
                          {overdueCheckIns.length} Check-in Overdue
                        </span>
                      </div>
                    )}
                    {overdueCheckOuts.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                        <span className="badge" style={{ 
                          background: '#ff6b6b',
                          padding: '8px',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}>
                          {overdueCheckOuts.length} Check-out Overdue
                        </span>
                      </div>
                    )}
                    {expiredBookings.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                        <span className="badge" style={{ 
                          background: '#f44336',
                          padding: '8px',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}>
                          {expiredBookings.length} Expired
                        </span>
                      </div>
                    )}
                    {almostDueCheckIns.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                        <span className="badge" style={{ 
                          background: '#ff9800',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}>
                          {almostDueCheckIns.length} Upcoming Check-in{almostDueCheckIns.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                    {almostDueCheckOuts.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                        <span className="badge" style={{ 
                          background: '#2196F3',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}>
                          {almostDueCheckOuts.length} Upcoming Check-out{almostDueCheckOuts.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>                  
                </div>
              )}
            </div>
          </div>

          {/* Search Results */}
          {searchTerm && filteredBookings.length > 0 && (
            <div className="reservation-display">
              <p className="size5 bold caps" style={{ marginBottom: '10px' }}>
                Search Results ({filteredBookings.length})
              </p>
              <div className="scroll-fixed scroll-fixed7">
                {filteredBookings.map((booking, index) => {                  
                  const isStillValidForCheckIn = () => {
                    if (booking.status !== 'pending') return false;

                    const now = new Date();
                    const checkoutDate = booking.checkOut?.toDate 
                      ? booking.checkOut.toDate() 
                      : new Date(booking.checkOut || booking.checkOutDate);

                    // Allow check-in until the very end of the checkout day
                    const checkoutEnd = new Date(checkoutDate);
                    checkoutEnd.setHours(23, 59, 59, 999);

                    return now <= checkoutEnd;
                  };

                  // Update button availability:
                  const canCheckIn   = booking.status === 'pending' && !isExpiredBooking(booking); // Can check in if not expired
                  const canEdit      = booking.status === 'pending' && !isExpiredBooking(booking); // Can edit if not expired
                  const canCancel    = booking.status === 'pending'; // Can always cancel pending bookings
                  const canCheckOut  = booking.status === 'checked-in'; // Can check out if checked in
                  const isOverdue    = isOverdueForCheckIn(booking) || isOverdueForCheckOut(booking); // For styling                  
                  const isExpired    = booking.status === 'pending' && !canCheckIn;                  

                  return (
                    <div
                      key={booking.id}
                      style={{
                        padding: '14px',
                        background: isExpired ? '#2a1f1f' : '#181818e7',
                        borderRadius: '8px',
                        marginBottom: '10px',
                        borderLeft: `5px solid ${getStatusColor(booking.status)}`,
                        position: 'relative',
                        opacity: isExpired ? 0.85 : 1,
                      }}
                    >                      
                      {/* Case 2: Expired (both times passed, not checked in) */}
                      {isExpiredBooking(booking) && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: 'rgba(244, 67, 54, 0.85)',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            zIndex: 1
                          }}
                          title="Both check-in and check-out times have passed"
                        >
                          <i className="fas fa-ban" style={{ marginRight: '4px' }}></i>
                          Expired
                        </div>
                      )}

                      {/* Case 1: Overdue Check-in (check-in passed, checkout not passed, not checked in) */}
                      {isOverdueForCheckIn(booking) && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            zIndex: 1
                          }}
                          title="Check-in time has passed but checkout time hasn't"
                        >
                          <i className="fas fa-exclamation-circle" style={{ marginRight: '4px' }}></i>
                          Check-in Overdue
                        </div>
                      )}

                      {/* Case 3: Overdue Check-out (checked in, checkout passed) */}
                      {isOverdueForCheckOut(booking) && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: '#ff6b6b',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            zIndex: 1
                          }}
                          title="Checked in but check-out time has passed"
                        >
                          <i className="fas fa-exclamation-triangle" style={{ marginRight: '4px' }}></i>
                          Check-out Overdue
                        </div>
                      )}

                      <div className="column">
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span className="bold size5">{index + 1}.</span>
                            <strong style={{ fontSize: '16px' }}>{booking.guestName}</strong>
                          </div>
                          <div style={{ marginTop: '6px', fontSize: '14px', color: '#ccc' }}>
                            Room {booking.roomNumber} • {booking.roomTypeName}<br />
                            {formatDateTime(booking.checkInDate)} → {formatDateTime(booking.checkOutDate)}
                          </div>
                          <div style={{ marginBottom: '6px', fontSize: '13px' }}>
                            <span style={{ color: getStatusColor(booking.status), fontWeight: 'bold' }}>
                              {getStatusText(booking.status)}
                              {isExpiredBooking(booking) && ' (Expired)'}
                              {isOverdueForCheckIn(booking) && ' (Check-in Overdue)'}
                              {isOverdueForCheckOut(booking) && ' (Check-out Overdue)'}
                            </span>
                          </div>
                        </div>

                        <div className="row justify-center">
                          <div className="row gap-15">

                            {/* Edit - only for still-valid pending bookings */}
                            {canEdit && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditModal(booking);
                                }}
                                className="list-btn"
                                title="Edit Reservation"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            )}

                            {/* Always available - print reservation */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadReservation(booking);
                              }}
                              className="list-btn"
                              title="Print Reservation"
                            >
                              <i className="fas fa-print"></i>
                            </button>

                            {/* Record Payment */}                                                        
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (booking.status === 'checked-out') {
                                  setError("Cannot record payments after checkout");
                                  setShowModal(true);
                                  return;
                                }
                                setSelectedBooking(booking);
                                setLinkedSales(booking.linkedSales || []);
                                const totalInc = (booking.linkedSales || []).reduce((sum, sale) => sum + (sale.grandTotal || 0), 0);
                                setIncidentalTotal(totalInc);
                                setPaymentAmount('');
                                setPayerName(booking.guestName);
                                setPaymentMethod('Transfer');
                                setPaymentNotes('');
                                setIsPaymentModalOpen(true);
                              }}
                              className="list-btn"
                              title="Record Payment"
                              disabled={booking.status === 'checked-out'}
                              style={{ display: booking.status === 'checked-out' ? 'none' : 'block' }}
                            >
                              <i className="fas fa-money-bill"></i>
                            </button>

                            {/* Payment History */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedBooking(booking);
                                
                                // Load linked sales
                                setLinkedSales(booking.linkedSales || []);
                                const totalInc = (booking.linkedSales || []).reduce((sum, sale) => sum + (sale.grandTotal || 0), 0);
                                setIncidentalTotal(totalInc);
                                
                                fetchPaymentHistory(booking);
                                setIsHistoryModalOpen(true);
                              }}
                              className="list-btn"
                              title="Payment History"
                            >
                              <i className="fas fa-history"></i>
                            </button>
                            {/* Check In - only if still within valid period */}
                            {canCheckIn && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedBooking(booking);
                                  setIsCheckInModalOpen(true);
                                }}
                                className="list-btn checkin-btn"
                                title="Check In Guest"
                                style={{ color: '#4CAF50' }}
                              >
                                <i className="fas fa-sign-in-alt"></i>
                              </button>
                            )}

                            {/* Cancel - available for all pending (even expired) */}
                            {canCancel && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedBooking(booking);
                                  setIsCancelConfirmModalOpen(true);
                                }}
                                className="list-btn cancel-btn"
                                title="Cancel Booking"
                                style={{ color: '#f44336' }}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            )}

                            {/* Check Out - for checked-in guests */}
                            {canCheckOut && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedBooking(booking);
                                  setIsCheckOutModalOpen(true);
                                }}
                                className="list-btn checkout-btn"
                                title="Check Out Guest"
                                style={{ color: '#FF9800' }}
                              >
                                <i className="fas fa-sign-out-alt"></i>
                              </button>
                            )}

                            {/* Final Bill - for checked-out */}
                            {booking.status === 'checked-out' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadGuestBill(booking);
                                }}
                                className="list-btn"
                                title="Download Final Bill"
                                style={{ color: '#9C27B0' }}
                              >
                                <i className="fas fa-file-invoice-dollar"></i>
                              </button>
                            )}

                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {searchTerm && filteredBookings.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              <p>No bookings found matching "{searchTerm}"</p>
            </div>
          )}

          {!searchTerm && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              <p>Enter a guest name, phone, room number, or room type to search for reservations</p>
            </div>
          )}
        </div>
      </div>

      {/* Record Payment Modal */}
      <Modal
        isOpen={isPaymentModalOpen}
        onRequestClose={() => setIsPaymentModalOpen(false)}
        className="custom-modal modal-scroll"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">Record Payment</h2>
        </div>
        <div className="modal-body">
          {selectedBooking && (
            <>
              <p>
                <span><strong>Guest:</strong> {selectedBooking.guestName}</span><br />
                <span><strong>Room:</strong> {selectedBooking.roomNumber} ({selectedBooking.roomTypeName})</span><br /><br />
                
                <strong>Room Charges:</strong> ₦{(selectedBooking.totalPrice || 0).toLocaleString()}<br />
                
                {incidentalTotal > 0 && (
                  <>
                    <strong>Incidental Charges:</strong> ₦{incidentalTotal.toLocaleString()}<br />
                    <div style={{ marginLeft: '16px', marginTop: '8px', fontSize: '0.95em', color: '#ddd' }}>
                      {linkedSales.map((sale, index) => (
                        <div key={sale.saleId || index} style={{ marginBottom: '12px' }}>
                          <div style={{ fontWeight: 'bold' }}>
                            <span className="bold font-gold">{index + 1}.</span> ₦{sale.grandTotal?.toLocaleString() || 0} — by {sale.cashierName || 'Staff'}
                          </div>
                          <ul style={{ margin: '4px 0 0 20px', listStyleType: 'disc', paddingLeft: 0 }}>
                            {sale.products?.map((p, i) => (
                              <li key={i}>
                                {p.name} × {p.quantity} @ ₦{p.unitPrice?.toLocaleString()} = ₦{p.total?.toLocaleString()}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                
                <br />
                <strong style={{ fontSize: '1.1em' }}>Total Due:</strong> ₦{((selectedBooking.totalPrice || 0) + incidentalTotal).toLocaleString()}<br />
                <strong>Paid So Far:</strong> ₦{(selectedBooking.deposit || 0).toLocaleString()}<br />
                <strong className="bold font-gold">
                  Balance Due:
                </strong> ₦{(((selectedBooking.totalPrice || 0) + incidentalTotal) - (selectedBooking.deposit || 0)).toLocaleString()}
              </p>
              <div className="desktop-form-input">
                <div className="column">
                  <label className="input-label">Amount Paid (₦)</label>
                  <input
                    type="number"
                    className="input-field"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="Enter amount"
                  />
                </div>
                <div className="column">
                  <label className="input-label">Payer Name</label>
                  <input
                    type="text"
                    className="input-field"
                    value={payerName}
                    onChange={(e) => setPayerName(e.target.value)}
                    placeholder="e.g. John Doe"
                  />
                </div>
              </div>
              
              <div className="form-group column">
                <label className="input-label">Payment Account</label>
                <select
                  className="input-field"
                  value={selectedPaymentAccountId}
                  onChange={(e) => setSelectedPaymentAccountId(e.target.value)}
                  required
                >
                  <option value="">Select Account</option>
                  {hotelAccounts
                    .filter(acc => acc.active !== false)
                    .map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.name} {acc.code ? `(${acc.code})` : ''}
                      </option>
                    ))}
                </select>
              </div>





              <div className="form-group column">
                <label className="input-label">Notes (Optional)</label>
                <textarea
                  className="input-field"
                  rows="3"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="e.g. Paid via bank transfer"
                />
              </div>

              <div className="row align-center justify-center gap-1 margin-top-20">
                <button className="login-button" onClick={() => setIsPaymentModalOpen(false)}>Cancel</button>
                <button className="login-button" onClick={handleRecordPayment}>Record Payment</button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Payment History Modal */}      
      <Modal
        isOpen={isHistoryModalOpen}
        onRequestClose={() => setIsHistoryModalOpen(false)}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">Payment History</h2>
        </div>

        <div className="modal-body">
          {selectedBooking && (
            <>
              <p className="margin-bottom-15">
                <strong>Guest:</strong> {selectedBooking.guestName}  
                <br />
                <strong>Room:</strong> {selectedBooking.roomNumber}
              </p>

              <div className="scroll-fixed extra-amenities history-display">
                {fetchingHistory ? (
                  <p className="text-center padding-20">Loading payment history...</p>
                ) : paymentHistory.length === 0 ? (
                  <p className="text-center padding-20 color-gray">
                    No payments recorded for this booking yet
                  </p>
                ) : (                  
                  paymentHistory.map((payment) => (
                    <div
                      key={payment.id}
                      className="info-item"
                      style={{
                        padding: '12px',
                        borderBottom: '1px solid #333',
                        marginBottom: '10px',
                        background: payment.voided ? '#2a1f1f' : '#222',
                        borderRadius: '6px',
                        position: 'relative',
                        opacity: payment.voided ? 0.7 : 1,
                      }}
                    >
                      {/* Payment details */}
                      <div style={{ opacity: payment.voided ? 0.6 : 1 }}>
                        <strong>₦{payment.amount.toLocaleString()}</strong><br />
                        <span>Method: <strong>{payment.paymentMethod}</strong></span><br />
                        <span>Payer: {payment.payerName}</span><br />
                        <span>Cashier: <strong>{payment.cashierName}</strong></span><br />
                        {payment.notes && <span>Notes: {payment.notes}</span>}<br />                        
                      </div>

                      <div className="align-center space-between">
                        <div className="column">
                          <span className="size5 bold font-gold">{formatDateTime(payment.timestamp)}</span>
                          {payment.voided && (
                            <span className="font-red bold size5">
                              VOIDED on {formatDateTime(payment.voidedAt?.toDate?.() || new Date())}
                            </span>
                          )}
                        </div>

                        {/* This is the actions area — make sure this part exists */}
                        <div className="row gap-15">
                          {!payment.voided && (
                            <button
                              onClick={() => {
                                generatePaymentReceiptPDF(
                                  payment,
                                  selectedBooking,
                                  payment.cashierName || 'Staff',
                                  incidentalTotal
                                );
                              }}
                              className="list-btn"
                              title="Print Receipt"
                            >
                              <i className="fas fa-print"></i>
                            </button>
                          )}

                          {/* === VOID BUTTON — Add / fix this block === */}
                          {!payment.voided && selectedBooking?.status !== 'checked-out' && (
                            <button
                              onClick={() => {
                                setPaymentToVoid(payment);
                                setIsVoidConfirmModalOpen(true);
                              }}
                              className="list-btn delete-btn"
                              title="Void this Payment"
                              style={{ color: '#f44336' }}
                            >
                              <i className="fas fa-trash-can"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="row align-center justify-center margin-top-25">
                <button 
                  className="login-button"
                  onClick={() => setIsHistoryModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
      
      <Modal
      isOpen={isEditModalOpen}
      // onRequestClose={() => setIsEditModalOpen(false)}
      onRequestClose={() => {
        setIsEditModalOpen(false);
        setAvailableEditRooms([]); // Reset available rooms
        setCheckingAvailability(false); // Reset loading state
      }}
      className="custom-modal modal-scroll"
      overlayClassName="custom-modal-overlay"
    >
      <div className="modal-header">
        <h2 className="size5 caps bold">Edit Booking</h2>
      </div>
      <div className="modal-body">
        <p className="caps size5 bold">{editForm.guestName || "—"} ({editForm.guestPhone || "—"})</p>
        
        {/* Price Summary */}                
        <div className="price-summary" style={{
          background: '#2a2a2a',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #444'
        }}>
          <p className="size5 bold" style={{ marginBottom: '12px' }}>PRICE BREAKDOWN</p>
          
          {editForm.roomId && editForm.ratePlanId && editForm.nights > 0 ? (
            <>
              <div className="row space-between" style={{ marginBottom: '8px' }}>
                <span>Room rate (₦{editForm.nightlyRate?.toLocaleString() || '0'}) × {editForm.nights} nights</span>
                <span>₦{((editForm.nightlyRate || 0) * editForm.nights).toLocaleString()}</span>
              </div>
              
              {editForm.selectedAmenities.length > 0 && (
                <div className="row space-between" style={{ marginBottom: '8px' }}>
                  <span>Extra amenities</span>
                  <span>₦{extraAmenitiesCost().toLocaleString()}</span>
                </div>
              )}

              <div className="row space-between" style={{ paddingTop: '10px', borderTop: '1px solid #555' }}>
                <span className="bold">Total Price</span>
                <span className="bold size4">₦{editForm.totalPrice.toLocaleString()}</span>
              </div>

              <div className="row space-between" style={{ marginTop: '12px' }}>
                <span>Deposit (already paid)</span>
                <span>₦{(editForm.deposit || 0).toLocaleString()}</span>
              </div>

              <div className="row space-between" style={{ marginTop: '10px' }}>
                <span className="bold">Balance Due</span>
                <span className={`bold ${editForm.totalPrice > editForm.deposit ? 'font-gold' : 'font-green'}`}>
                  ₦{(editForm.totalPrice - (editForm.deposit || 0)).toLocaleString()}
                </span>
              </div>
            </>
          ) : (
            <div style={{ color: '#888', fontStyle: 'italic', marginBottom: '12px' }}>
              {!editForm.roomId ? 'Select a room' : !editForm.ratePlanId ? 'Select a rate plan' : 'Select check-in and check-out dates'}
            </div>
          )}
        </div>

        <div className="desktop-form-input" style={{ marginBottom: '15px' }}>
          <div>
            <button
              type="button"
              className="input-field"
              onClick={() => {
                if (editForm.checkIn && editForm.checkOut) {
                  const currentRoom = rooms.find(r => r.id === editForm.roomId);
                  const roomTypeId = currentRoom?.roomTypeId;
                  fetchAvailableRoomsForEdit(editForm.checkIn, editForm.checkOut, roomTypeId);
                } else {
                  setError("Please select check-in and check-out dates first");
                  setShowModal(true);
                }
              }}
              disabled={checkingAvailability || !editForm.checkIn || !editForm.checkOut}
              style={{ 
                padding: '8px 16px',
                fontSize: '14px',
                backgroundColor: '#2a2a2a',
                marginBottom: '0px'
              }}
            >
              {checkingAvailability ? 'Checking...' : 'Check Room Availability'}
            </button>
          </div>
          <div>
            {editForm.checkIn && editForm.checkOut && availableEditRooms.length > 0 && (
              <p className="font-gold">
                {availableEditRooms.length} room{availableEditRooms.length !== 1 ? 's' : ''} available
              </p>
            )}
          </div>
        </div>

        <div className="desktop-form-input">
          <div className="form-group column">
            <label className="input-label">Room</label>            
            {checkingAvailability ? (
              <div style={{ textAlign: 'center', padding: '10px', color: '#ffb267' }}>
                Checking room availability...
              </div>
            ) : (
              <select
                className="input-field"
                value={editForm.roomId}
                onChange={async (e) => {
                  const newRoomId = e.target.value;
                  const room = rooms.find(r => r.id === newRoomId);
                  const roomType = roomTypes.find(rt => rt.id === room?.roomTypeId);
                  
                  // Filter amenities to exclude those already included in the room type
                  const includedAmenities = roomType?.amenityIds || [];
                  const filteredAmenities = editForm.selectedAmenities.filter(
                    amenityId => !includedAmenities.includes(amenityId)
                  );
                  
                  setEditForm({ 
                    ...editForm, 
                    roomId: newRoomId,
                    ratePlanId: '', // Reset rate plan when room changes
                    selectedAmenities: filteredAmenities
                  });
                }}
                disabled={!editForm.checkIn || !editForm.checkOut || checkingAvailability}
              >
                <option value="">Select Room</option>
                
                {/* Current room (always show even if not in available list) */}
                {selectedBooking?.roomId && (
                  <option value={selectedBooking.roomId}>
                    Current: Room {selectedBooking.roomNumber} 
                    {availableEditRooms.some(r => r.id === selectedBooking.roomId) 
                      ? ' (Available)' 
                      : ' (Currently booked)'}
                  </option>
                )}
                
                {/* Available rooms */}
                {availableEditRooms
                  .filter(room => room.id !== selectedBooking?.roomId) // Don't duplicate current room
                  .map(room => (
                    <option key={room.id} value={room.id}>
                      Room {room.roomNumber} - {room.roomTypeName || 'Unknown'}
                    </option>
                  ))
                }
                
                {/* Show message if no rooms available */}
                {availableEditRooms.length === 0 && editForm.checkIn && editForm.checkOut && (
                  <option value="" disabled>
                    No rooms available for selected dates
                  </option>
                )}
              </select>
            )}
            
            {(!editForm.checkIn || !editForm.checkOut) && (
              <small style={{ color: '#ffb267', marginTop: '5px' }}>
                Select check-in and check-out dates first to see available rooms
              </small>
            )}
                        
          </div>
          <div className="form-group column">
            <label className="input-label">Rate Plan</label>
            <select
              className="input-field"
              value={editForm.ratePlanId}
              onChange={(e) => setEditForm({ ...editForm, ratePlanId: e.target.value })}
            >
              <option value="">Select Rate Plan</option>
              {ratePlans.map(rp => (
                <option key={rp.id} value={rp.id}>
                  {rp.name} {rp.rateAdjustment ? `(${rp.rateAdjustment > 0 ? '+' : ''}${rp.rateAdjustment}%)` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="desktop-form-input">
          <div className="form-group column">
            <label className="input-label">Check-in Date</label>
            <input
              type="datetime-local"
              className="input-field"
              value={editForm.checkIn ? new Date(editForm.checkIn?.toDate ? editForm.checkIn.toDate() : editForm.checkIn).toISOString().slice(0, 16) : ""}
              onChange={(e) => setEditForm({ 
                ...editForm, 
                checkIn: Timestamp.fromDate(new Date(e.target.value)) 
              })}
            />
          </div>
          <div className="form-group column">
            <label className="input-label">Check-out Date</label>
            <input
              type="datetime-local"
              className="input-field"
              value={editForm.checkOut ? new Date(editForm.checkOut?.toDate ? editForm.checkOut.toDate() : editForm.checkOut).toISOString().slice(0, 16) : ""}
              onChange={(e) => setEditForm({ 
                ...editForm, 
                checkOut: Timestamp.fromDate(new Date(e.target.value)) 
              })}
            />
          </div>
        </div>

        {/* Add a nights counter */}
        {editForm.checkIn && editForm.checkOut && (
          <div className="nights-counter" style={{
            textAlign: 'center',
            padding: '8px',
            background: '#333',
            borderRadius: '4px',
            marginBottom: '15px'
          }}>
            <span className="bold">
              {Math.ceil((
                (editForm.checkOut?.toDate ? editForm.checkOut.toDate() : new Date(editForm.checkOut)) - 
                (editForm.checkIn?.toDate ? editForm.checkIn.toDate() : new Date(editForm.checkIn))
              ) / (1000 * 60 * 60 * 24))} Nights
            </span>
          </div>
        )}

        {getExtraAmenitiesForEdit().length > 0 && (
          <div className="form-group column">
            <label className="input-label">Extra Amenities</label>
            <div className="extra-amenities">
              {getExtraAmenitiesForEdit().map(amenity => (
                <label key={amenity.id} style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    className='gold-tick'
                    checked={editForm.selectedAmenities.includes(amenity.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditForm({
                          ...editForm,
                          selectedAmenities: [...editForm.selectedAmenities, amenity.id]
                        });
                      } else {
                        setEditForm({
                          ...editForm,
                          selectedAmenities: editForm.selectedAmenities.filter(id => id !== amenity.id)
                        });
                      }
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  <strong>{amenity.name}</strong> (+₦{amenity.price?.toLocaleString() || '0'}/night)
                </label>
              ))}
            </div>
            {editForm.selectedAmenities.length > 0 && editForm.checkIn && editForm.checkOut && (
              <div style={{ marginTop: '10px', fontSize: '14px', color: '#aaa' }}>
                <small>
                  Extra amenities cost: ₦{
                    (editForm.selectedAmenities.reduce((total, amenityId) => {
                      const amenity = amenities.find(a => a.id === amenityId);
                      const nights = editForm.checkIn && editForm.checkOut 
                        ? Math.ceil((
                            (editForm.checkOut?.toDate ? editForm.checkOut.toDate() : new Date(editForm.checkOut)) - 
                            (editForm.checkIn?.toDate ? editForm.checkIn.toDate() : new Date(editForm.checkIn))
                          ) / (1000 * 60 * 60 * 24))
                        : 0;
                      return total + ((amenity?.price || 0) * nights);
                    }, 0)).toLocaleString()
                  } ({editForm.selectedAmenities.length} amenities × {
                    editForm.checkIn && editForm.checkOut 
                      ? Math.ceil((
                          (editForm.checkOut?.toDate ? editForm.checkOut.toDate() : new Date(editForm.checkOut)) - 
                          (editForm.checkIn?.toDate ? editForm.checkIn.toDate() : new Date(editForm.checkIn))
                        ) / (1000 * 60 * 60 * 24))
                      : 0
                  } nights)
                </small>
              </div>
            )}
          </div>
        )}                
        
        <div className="form-group column">
          <label className="input-label">Notes</label>
          <textarea
            className="input-field"
            rows="4"
            value={editForm.notes}
            onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
            placeholder="Special requests, additional info..."
          />
        </div>
        
        <div className="row align-center justify-center gap-1 margin-top-20">
          <button className="login-button" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
          <button className="login-button" onClick={handleEditBooking}>Save Changes</button>
        </div>
      </div>
    </Modal>
      
      {/* Check In Confirmation Modal */}
      <Modal
        isOpen={isCheckInModalOpen}
        onRequestClose={() => setIsCheckInModalOpen(false)}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">Confirm Check In</h2>
        </div>
        <div className="modal-body">
          {selectedBooking && (
            <>
              <p>
                <span><strong>Guest:</strong> {selectedBooking.guestName}</span><br />
                <span><strong>Phone:</strong> {selectedBooking.guestPhone}</span><br />
                <span><strong>Room:</strong> {selectedBooking.roomNumber} ({selectedBooking.roomTypeName})</span><br />
                <span><strong>Duration:</strong> {formatDateTime(selectedBooking.checkInDate)} <span className="font-gold bold">to</span> {formatDateTime(selectedBooking.checkOutDate)}</span>
              </p>              
              
              <div className="row align-center justify-center gap-1 margin-top-20">
                <button className="login-button" onClick={() => setIsCheckInModalOpen(false)}>
                  Cancel
                </button>
                <button 
                  className="login-button" 
                  onClick={handleCheckIn}
                  style={{ backgroundColor: '#2196F3' }}
                >
                  Check In
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Check Out Confirmation Modal */}
      <Modal
        isOpen={isCheckOutModalOpen}
        onRequestClose={() => setIsCheckOutModalOpen(false)}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">Confirm Check Out</h2>
        </div>
        <div className="modal-body">
          {selectedBooking && (
            <>
              <p>
                <span><strong>Guest:</strong> {selectedBooking.guestName}</span><br />
                <span><strong>Phone:</strong> {selectedBooking.guestPhone}</span><br />
                <span><strong>Room:</strong> {selectedBooking.roomNumber} ({selectedBooking.roomTypeName})</span><br />
                <span><strong>Duration:</strong> {formatDateTime(selectedBooking.checkInDate)} <span className="font-gold bold">to</span> {formatDateTime(selectedBooking.checkOutDate)}</span>
              </p>
              <p>
                <span><strong>Total Price:</strong> ₦{selectedBooking.totalPrice?.toLocaleString() || '0'}</span><br />
                <span><strong>Deposit:</strong> ₦{selectedBooking.deposit?.toLocaleString() || '0'}</span><br />
                <span><strong>Balance Due:</strong> ₦{selectedBooking.balance?.toLocaleString() || '0'}</span><br />                
              </p>                                         
              
              <div className="row align-center justify-center gap-1 margin-top-20">
                <button className="login-button" onClick={() => setIsCheckOutModalOpen(false)}>
                  Cancel
                </button>
                <button 
                  className="login-button" 
                  onClick={handleCheckOut}
                  style={{ backgroundColor: '#9C27B0' }}
                >
                  Check Out
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Cancel Booking Confirmation Modal */}      
      <Modal
        isOpen={isCancelConfirmModalOpen}
        onRequestClose={() => {
          setIsCancelConfirmModalOpen(false);
          setCancelReason(''); // reset when closing
        }}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">Cancel Booking</h2>
        </div>
        <div className="modal-body">
          {selectedBooking && (
            <>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '50px', color: '#fc796f' }}></i>
              </div>
              
              <p style={{ textAlign: 'center', fontSize: '18px', marginBottom: '20px' }}>
                Are you sure you want to cancel this booking?
              </p>

              <div className="form-group column" style={{ margin: '20px 0' }}>
                <label className="input-label">Reason for Cancellation</label>
                <textarea
                  className="input-field"
                  rows="3"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g. Guest no-show, change of plans, double booking..."
                />                
              </div>

              <p>
                <strong>Guest:</strong> {selectedBooking.guestName}<br />
                <strong>Phone:</strong> {selectedBooking.guestPhone}<br />
                <strong>Room:</strong> {selectedBooking.roomNumber} ({selectedBooking.roomTypeName})<br />
                <strong>Duration:</strong> {formatDateTime(selectedBooking.checkInDate)} → {formatDateTime(selectedBooking.checkOutDate)}<br />
                <strong>Total Price:</strong> ₦{selectedBooking.totalPrice?.toLocaleString() || '0'}
              </p>

              <div className="row align-center justify-center gap-1 margin-top-20">
                <button className="login-button" onClick={() => {
                  setIsCancelConfirmModalOpen(false);
                  setCancelReason('');
                }}>
                  Keep Booking
                </button>
                <button 
                  className="login-button delete-btn" 
                  onClick={handleCancelBookingConfirm}
                  style={{ backgroundColor: '#fc796f' }}
                >
                  Cancel Booking
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Overdue Bookings Popup Modal */}
      <Modal
        isOpen={showOverduePopup}
        onRequestClose={() => setShowOverduePopup(false)}
        className="custom-modal modal-scroll"
        overlayClassName="custom-modal-overlay"       
      >
        <div className="modal-header">
          <h2 className="size5 caps bold" style={{ color: 'white' }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: '10px' }}></i>
            Action Required - Overdue & Upcoming Bookings
          </h2>
        </div>
        
        <div className="modal-body">
          {/* Overdue Check-ins Section */}
          {overdueCheckIns.length > 0 && (
            <div>
              <p className="caps size5" style={{color: '#2196F3'}}>
                <i className="fas fa-exclamation-circle"></i> OVERDUE CHECK-INS ({overdueCheckIns.length})
              </p>
              <div className="scroll-fixed" style={{ maxHeight: '200px' }}>
                {overdueCheckIns.map((booking, index) => {
                  const checkInTime = booking.checkIn?.toDate?.() || new Date(booking.checkIn);
                  const hoursLate = Math.floor((new Date() - checkInTime) / (1000 * 60 * 60));
                  
                  return (
                    <div key={booking.id} style={{
                      background: 'rgba(70, 70, 70, 0.1)',
                      padding: '12px',
                      marginBottom: '10px',
                      borderRadius: '4px'
                    }}>
                      <div className="row space-between">
                        <div>
                          <strong>{booking.guestName}</strong>
                          <div>
                            Room {booking.roomNumber} • {booking.roomTypeName}
                          </div>
                          <div className="size5">
                            <i className="far fa-clock"></i> Check-in was due: {formatDateTime(checkInTime)}
                          </div>
                          <div className="font-gold size5 bold">
                            {hoursLate} hour{hoursLate !== 1 ? 's' : ''} overdue
                          </div>
                        </div>
                        <div>
                          <button
                            onClick={() => {
                              setSelectedBooking(booking);
                              setIsCheckInModalOpen(true);
                              setShowOverduePopup(false);
                            }}
                            className="list-btn"
                            style={{backgroundColor: '#2196F3'}}                         
                          >
                            <i className="fas fa-sign-in-alt"></i> Check In Now
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Expired Bookings Section */}
          {expiredBookings.length > 0 && (
            <div>
              <p className="size5 caps" style={{color: '#fc796f'}}>
                <i className="fas fa-ban"></i> EXPIRED BOOKINGS ({expiredBookings.length})
              </p>
              <div className="scroll-fixed" style={{ maxHeight: '200px' }}>
                {expiredBookings.map((booking, index) => {
                  const checkInTime = booking.checkIn?.toDate?.() || new Date(booking.checkIn);
                  const checkOutTime = booking.checkOut?.toDate?.() || new Date(booking.checkOut);
                  const hoursSinceCheckOut = Math.floor((new Date() - checkOutTime) / (1000 * 60 * 60));
                  
                  return (
                    <div key={booking.id} style={{
                      background: 'rgba(70, 70, 70, 0.1)',
                      padding: '12px',
                      marginBottom: '10px',
                      borderRadius: '4px'
                    }}>
                      <div className="row space-between">
                        <div>
                          <strong>{booking.guestName}</strong>
                          <div>
                            Room {booking.roomNumber} • {booking.roomTypeName}
                          </div>
                          <div className="size5">
                            <i className="far fa-clock"></i> Check-in was: {formatDateTime(checkInTime)}
                          </div>
                          <div className="size5">
                            <i className="far fa-clock"></i> Check-out was: {formatDateTime(checkOutTime)}
                          </div>
                          <div className="font-gold size5 bold">
                            Expired {hoursSinceCheckOut} hour{hoursSinceCheckOut !== 1 ? 's' : ''} ago
                          </div>
                        </div>
                        <div>
                          <button
                            onClick={() => {
                              setSelectedBooking(booking);
                              setIsCancelConfirmModalOpen(true);
                              setShowOverduePopup(false);
                            }}
                            className="list-btn"
                            style={{ background: '#fc796f' }}
                          >
                            <i className="fas fa-times"></i> Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Overdue Check-outs Section */}
          {overdueCheckOuts.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <p className="size5 caps" style={{color: '#9C27B0'}}>
                <i className="fas fa-exclamation-triangle"></i> OVERDUE CHECK-OUTS ({overdueCheckOuts.length})
              </p>
              <div className="scroll-fixed" style={{ maxHeight: '200px' }}>
                {overdueCheckOuts.map((booking, index) => {
                  const checkOutTime = booking.checkOut?.toDate?.() || new Date(booking.checkOut);
                  const hoursLate = Math.floor((new Date() - checkOutTime) / (1000 * 60 * 60));
                  
                  return (
                    <div key={booking.id} style={{
                      background: 'rgba(70, 70, 70, 0.1)',
                      padding: '12px',
                      marginBottom: '10px',
                      borderRadius: '4px'
                    }}>
                      <div className="row space-between">
                        <div>
                          <strong>{booking.guestName}</strong>
                          <div>
                            Room {booking.roomNumber} • {booking.roomTypeName}
                          </div>
                          <div className="size5">
                            <i className="far fa-clock"></i> Check-out was due: {formatDateTime(checkOutTime)}
                          </div>
                          <div className="size5 bold font-gold">
                            {hoursLate} hour{hoursLate !== 1 ? 's' : ''} overdue
                          </div>
                        </div>
                        <div>
                          <button
                            onClick={() => {
                              setSelectedBooking(booking);
                              setIsCheckOutModalOpen(true);
                              setShowOverduePopup(false);
                            }}
                            className="list-btn"
                            style={{ background: '#9C27B0' }}
                          >
                            <i className="fas fa-sign-out-alt"></i> Check Out Now
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Upcoming Check-ins Section */}
          {almostDueCheckIns.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ color: '#ff9800', marginBottom: '15px', borderBottom: '2px solid #ff9800', paddingBottom: '5px' }}>
                <i className="fas fa-clock"></i> UPCOMING CHECK-INS ({almostDueCheckIns.length})
              </h3>
              <div className="scroll-fixed" style={{ maxHeight: '150px' }}>
                {almostDueCheckIns.map((booking, index) => {
                  const checkInTime = booking.checkIn?.toDate?.() || new Date(booking.checkIn);
                  const hoursUntil = Math.floor((checkInTime - new Date()) / (1000 * 60 * 60));
                  
                  return (
                    <div key={booking.id} style={{
                      background: 'rgba(255, 152, 0, 0.1)',
                      borderLeft: '4px solid #ff9800',
                      padding: '10px',
                      marginBottom: '8px',
                      borderRadius: '4px'
                    }}>
                      <div className="row space-between">
                        <div>
                          <strong>{booking.guestName}</strong>
                          <div style={{ fontSize: '14px', color: '#ddd', marginTop: '4px' }}>
                            Room {booking.roomNumber} • {booking.roomTypeName}
                          </div>
                          <div style={{ fontSize: '13px', color: '#ffb267', marginTop: '4px' }}>
                            <i className="far fa-clock"></i> Check-in at: {formatDateTime(checkInTime)}
                          </div>
                          <div style={{ fontSize: '12px', color: isUrgentCheckIn(booking) ? '#ff5722' : '#ffb267', marginTop: '2px' }}>
                            {hoursUntil <= 0 ? 'Due now' : `${hoursUntil} hour${hoursUntil !== 1 ? 's' : ''} from now`}
                            {isUrgentCheckIn(booking) && ' ⚠️ URGENT'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Upcoming Check-outs Section */}
          {almostDueCheckOuts.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: '#2196F3', marginBottom: '15px', borderBottom: '2px solid #2196F3', paddingBottom: '5px' }}>
                <i className="fas fa-clock"></i> UPCOMING CHECK-OUTS ({almostDueCheckOuts.length})
              </h3>
              <div className="scroll-fixed" style={{ maxHeight: '150px' }}>
                {almostDueCheckOuts.map((booking, index) => {
                  const checkOutTime = booking.checkOut?.toDate?.() || new Date(booking.checkOut);
                  const hoursUntil = Math.floor((checkOutTime - new Date()) / (1000 * 60 * 60));
                  
                  return (
                    <div key={booking.id} style={{
                      background: 'rgba(33, 150, 243, 0.1)',
                      borderLeft: '4px solid #2196F3',
                      padding: '10px',
                      marginBottom: '8px',
                      borderRadius: '4px'
                    }}>
                      <div className="row space-between">
                        <div>
                          <strong>{booking.guestName}</strong>
                          <div style={{ fontSize: '14px', color: '#ddd', marginTop: '4px' }}>
                            Room {booking.roomNumber} • {booking.roomTypeName}
                          </div>
                          <div style={{ fontSize: '13px', color: '#64b5f6', marginTop: '4px' }}>
                            <i className="far fa-clock"></i> Check-out at: {formatDateTime(checkOutTime)}
                          </div>
                          <div style={{ fontSize: '12px', color: isUrgentCheckOut(booking) ? '#ff5722' : '#64b5f6', marginTop: '2px' }}>
                            {hoursUntil <= 0 ? 'Due now' : `${hoursUntil} hour${hoursUntil !== 1 ? 's' : ''} from now`}
                            {isUrgentCheckOut(booking) && ' ⚠️ URGENT'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {overdueCheckIns.length === 0 && overdueCheckOuts.length === 0 && 
          almostDueCheckIns.length === 0 && almostDueCheckOuts.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              <i className="fas fa-check-circle" style={{ fontSize: '48px', color: '#4CAF50', marginBottom: '20px' }}></i>
              <p>All good! No overdue or upcoming actions required.</p>
            </div>
          )}
          
          <div className="row align-center justify-center margin-top-30">
            <button 
              className="login-button"
              onClick={() => setShowOverduePopup(false)}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isSettleToAccountModalOpen}
        onRequestClose={() => setIsSettleToAccountModalOpen(false)}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">Outstanding Balance - Transfer to Account</h2>
        </div>
        <div className="modal-body">
          {selectedBooking && (
            <>
              <p>
                <strong>Guest:</strong> {selectedBooking.guestName}<br />
                <strong>Balance Due:</strong> ₦{(((selectedBooking.totalPrice || 0) + incidentalTotal) - (selectedBooking.deposit || 0)).toLocaleString()}
              </p>

              <div className="form-group column" style={{ margin: '20px 0' }}>
                <label className="input-label">Select Account</label>
                <select
                  className="input-field"
                  value={selectedAccountId}
                  onChange={e => setSelectedAccountId(e.target.value)}
                >
                  <option value="">Select Account</option>
                  {accounts
                    .filter(acc => acc.active !== false)
                    .map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.name} ({acc.code ? `${acc.code}` : ''})
                        {acc.currentBalance ? `  •  ₦${acc.currentBalance.toLocaleString()}` : ''}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group column" style={{ margin: '20px 0' }}>
                <label className="input-label">Batch (Optional)</label>
                <select
                  className="input-field"
                  value={selectedBatchId}
                  onChange={e => setSelectedBatchId(e.target.value)}
                >
                  <option value="">No Batch</option>
                  {batches.map(batch => (
                    <option key={batch.id} value={batch.id}>
                      {batch.name} {batch.batchCode ? `(${batch.batchCode})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group column">
                <label className="input-label">Memo / Reference</label>
                <textarea
                  className="input-field"
                  rows="2"
                  value={transferMemo}
                  onChange={e => setTransferMemo(e.target.value)}
                  placeholder="e.g. Company direct bill – ABC Travels"
                />
              </div>

              <div className="row align-center justify-center gap-1 margin-top-25">
                <button className="login-button" onClick={() => setIsSettleToAccountModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="login-button"
                  onClick={handleTransferToAccountAndCheckout}
                  disabled={!selectedAccountId || update}
                >
                  Transfer Balance & Check Out
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Void Confirmation Modal */}
      <Modal
        isOpen={isVoidConfirmModalOpen}
        onRequestClose={() => setIsVoidConfirmModalOpen(false)}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">Confirm Void Payment</h2>
        </div>
        <div className="modal-body">
          {paymentToVoid && (
            <>
              <p style={{ textAlign: 'center', marginBottom: '20px' }}>
                Are you sure you want to <strong>void</strong> this payment?<br />
                <strong>₦{paymentToVoid.amount.toLocaleString()}</strong> via {paymentToVoid.paymentMethod}<br />
                This action cannot be undone.
              </p>
              <div className="row align-center justify-center gap-1 margin-top-20">
                <button
                  className="login-button"
                  onClick={() => {
                    setIsVoidConfirmModalOpen(false);
                    setPaymentToVoid(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="login-button delete-btn"
                  onClick={handleVoidPayment}
                  disabled={update}
                >
                  Void Payment
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Success / Error Modal */}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}
    </>
  );
};

export default Reservation;